import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createDemoTenant } from '../../../api/demoTenants.api';

const inputCls =
  'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 bg-white';

type FormState = {
  name: string;
  email: string;
  password: string;
  phone: string;
  days: number;
};

const defaultForm: FormState = {
  name: '',
  email: '',
  password: '',
  phone: '',
  days: 3,
};

export type CreatedDemoResult = {
  email: string;
  password: string;
  tenantName: string;
  loginUrl: string;
  days: number;
};

interface CreateDemoTenantModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (result: CreatedDemoResult) => void;
}

export default function CreateDemoTenantModal({
  open,
  onClose,
  onCreated,
}: CreateDemoTenantModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(defaultForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const set = <K extends keyof FormState>(key: K, val: FormState[K]) => {
    setForm((p) => ({ ...p, [key]: val }));
    setError('');
  };

  const handleClose = () => {
    if (loading) return;
    setForm(defaultForm);
    setError('');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const r = await createDemoTenant({
      name: form.name.trim(),
      email: form.email.trim(),
      password: form.password,
      phone: form.phone.trim() || undefined,
      days: form.days,
    });
    setLoading(false);
    if (r.ok === false) {
      if (r.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(r.message);
      return;
    }
    onCreated({
      email: r.data.admin.email,
      password: r.data.admin.password,
      tenantName: r.data.tenant?.name || form.name,
      loginUrl: r.data.login_url,
      days: r.data.demo_days ?? form.days,
    });
    setForm(defaultForm);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={handleClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-base font-bold text-gray-900">{t('demo_tenants.create.title')}</h3>
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="p-6 space-y-4">
          <div className="rounded-xl bg-amber-50 border border-amber-100 px-3 py-2.5 text-xs text-amber-900 leading-relaxed">
            {t('demo_tenants.create.hint')}
          </div>

          {(
            [
              ['name', 'text'],
              ['email', 'email'],
              ['password', 'password'],
              ['phone', 'text'],
            ] as const
          ).map(([key, type]) => (
            <div key={key}>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                {t(`demo_tenants.create.fields.${key}`)}
              </label>
              <input
                type={type}
                required={key !== 'phone'}
                minLength={key === 'password' ? 6 : undefined}
                value={form[key]}
                onChange={(e) => set(key, e.target.value)}
                disabled={loading}
                className={inputCls}
              />
            </div>
          ))}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              {t('demo_tenants.create.fields.days')}
            </label>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                <button
                  key={d}
                  type="button"
                  disabled={loading}
                  onClick={() => set('days', d)}
                  className={`w-10 h-10 rounded-lg text-sm font-bold cursor-pointer transition-colors ${
                    form.days === d
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">{t('demo_tenants.create.days_help', { days: form.days })}</p>
          </div>

          {error ? (
            <p className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{error}</p>
          ) : null}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
            >
              {t('demo_tenants.create.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 cursor-pointer disabled:opacity-50"
            >
              {loading ? t('demo_tenants.create.submitting') : t('demo_tenants.create.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
